import { useEffect, useState } from 'react';
import { apiGet, withKey } from 'Shared/api';
import { useAuth } from 'Utils/Hooks/useAuth';

const P = process.env.PUBLIC_URL || '';
const safe = (p) => p.then((r) => r?.data).catch(() => null);

/**
 * Getting-started steps for the signed-in user, with "done" worked out from their real data
 * where that's cheap. Returns {steps, loading}; each step: {key, title, text, to, help, done, count}.
 */
export default function useChecklist(audience, active = true) {
  const { currentAdmin } = useAuth();
  const [state, setState] = useState({ steps: [], loading: true });

  useEffect(() => {
    if (!active || !currentAdmin) return undefined;
    let alive = true;
    const load = async () => {
      let steps = [];
      if (audience === 'admin') {
        const [accounts, waiting, issues, gcms] = await Promise.all([
          safe(apiGet('/admin/verification/accounts', { status: 'pending_verification' })),
          safe(apiGet('/admin/onboarding/services', { awaiting_review: true })),
          safe(apiGet('/admin/ops/issues', { status: 'open', per_page: 1 })),
          safe(apiGet('/admin/gcms/integrations', { status: 'queue,provisioning' })),
        ]);
        const gcmsRows = gcms?.rows || [];
        const readyAccounts = (accounts || []).filter((a) => a.items_awaiting_review > 0 || a.ready_for_approval).length;
        steps = [
          { key: 'issues', title: 'Check open issues', text: 'Problems found by the nightly check, most severe first.', to: '/dashboard/ops/issues', help: 'admin-daily', count: issues?.total, done: issues ? !issues.total : undefined },
          { key: 'kyc', title: 'Review company verification', text: 'Partners waiting for their documents to be checked.', to: '/dashboard/accounts', help: 'admin-kyc', count: readyAccounts, done: accounts ? readyAccounts === 0 : undefined },
          { key: 'services', title: 'Review service onboarding', text: 'Applications partners have sent for review.', to: '/dashboard/services', help: 'admin-onboarding', count: waiting?.length, done: waiting ? waiting.length === 0 : undefined },
          { key: 'gcms', title: 'Review GCMS requests', text: 'Requests to run a GCMS service, and any provisioning that stopped.', to: '/dashboard/gcms', help: 'admin-gcms', count: gcmsRows.length, done: gcms ? gcmsRows.length === 0 : undefined },
          { key: 'overview', title: 'Read the overview', text: 'Subscriber base and this week’s figures across all partners.', to: '/dashboard/home', help: 'subscriber-base' },
          { key: 'ips', title: 'Approve network IPs', text: 'New addresses a network started calling from.', to: '/dashboard/ops/network-ips', help: 'admin-ips' },
        ];
      } else if (audience === 'pending') {
        const ver = await safe(apiGet('/partner/verification'));
        const items = (ver?.items || []).filter((v) => v.item.required);
        const sent = items.filter((v) => ['submitted', 'verified'].includes(v.status)).length;
        steps = [
          { key: 'verify', title: 'Verify your company', text: items.length ? `${sent} of ${items.length} items sent. Send each as you have it.` : 'CAC details, contact person, BVN or NIN and a recent utility bill.', to: '/partner/verification', help: 'verify-company', done: items.length > 0 && sent === items.length },
          { key: 'sandbox', title: 'Get your sandbox key', text: 'Start building your integration while we review your documents.', to: '/partner/sandbox', help: 'sandbox' },
          { key: 'api', title: 'Read how the API works', text: 'Subscriptions, opt-outs and the notifications we send you.', to: '/partner/help/api', help: 'api' },
        ];
      } else {
        const [products, apps, key, gcms] = await Promise.all([safe(apiGet('/product')), safe(apiGet('/partner/onboarding/services')), safe(apiGet('/partner/sandbox/key')), safe(apiGet('/partner/gcms/integrations'))]);
        const gcmsLive = Array.isArray(gcms) ? gcms.some((g) => g.status === 'live') : undefined;
        let tried;
        if (key?.sandbox_api_key) {
          const d = await safe(apiGet('/partner/sandbox/deliveries', { limit: 1 }, withKey(key.sandbox_api_key)));
          tried = Array.isArray(d) ? d.length > 0 : undefined;
        }
        const list = products || [];
        const hooked = list.some((p) => p.webhook || p.sync_webhook);
        const sent = (apps || []).some((a) => a.submitted_for_review_at);
        const live = (apps || []).some((a) => (a.telcos || []).some((t) => t.stage === 'approved'));
        steps = [
          { key: 'verify', title: 'Verify your company', text: 'Done. Your account is approved.', to: '/partner/verification', help: 'verify-company', done: true },
          { key: 'product', title: 'Create a product', text: 'Price, validity and billing for each service.', to: '/partner/product/create-product', help: 'create-product', done: products ? list.length > 0 : undefined },
          { key: 'webhook', title: 'Set your webhooks', text: 'Where we send subscriptions, renewals and opt-outs.', to: '/partner/product/product-list', help: 'webhooks', done: products ? hooked : undefined },
          { key: 'sandbox', title: 'Test in the sandbox', text: 'Subscribe a test number and watch your webhook receive it.', to: '/partner/sandbox', help: 'sandbox', done: tried },
          { key: 'onboard', title: 'Onboard with the networks', text: 'Send each network’s documents for review.', to: '/partner/services', help: 'service-onboarding', done: apps ? sent : undefined },
          { key: 'live', title: 'Go live', text: 'A network has approved one of your services.', to: '/partner/services', help: 'service-onboarding', done: apps ? live : undefined },
          { key: 'gcms', title: 'Add a GCMS service', text: 'Voting, quizzes, content or USSD menus on your shortcode, set up by Eudor.', to: '/partner/gcms', help: 'gcms-services', done: gcmsLive },
        ];
      }
      if (alive) setState({ steps: steps.map((s) => ({ ...s, to: `${P}${s.to}` })), loading: false });
    };
    load();
    return () => {
      alive = false;
    };
  }, [audience, active, currentAdmin]);

  return state;
}
